import { lazy } from 'react';
import { RiDeleteBinLine, RiEditLine, RiInstanceLine } from 'react-icons/ri';

import ToggleActive from '@lib/core/src/components/ToggleActive';
import palette from '@lib/core/src/theme/palette';
import { useQueryClient } from '@tanstack/react-query';

import { WorkflowDtoStatus, WorkflowResponse, getListWorkflowsQueryKey } from '@/api/generated';
import { LocalCapabilityType, triggerDisplay } from '@/components/AiWorkflow/formSchema';
import { useWorkflow } from '@/components/AiWorkflow/hooks/useWorkflow';
import useModal from '@/hooks/useModal/useModal';
import { createdFromToIcon } from '@/pages/JamiesCapabilities/featuredTemplatesData';
import { stringToTime } from '@/utils/dateUtil';

import {
  CapabilityRow,
  Cell,
  Text,
  SmallText,
  IconButton,
  Row,
  NameStack,
  IconContainer
} from './MyCapabilitiesStyles';

const LazyAreYouSureModal = lazy(
  () => import('@/components/modals/AreYouSureModal/AreYouSureModal')
);

interface MyCapabilitiesRowProps extends LocalCapabilityType {
  handleToggle: (agentKey: string | undefined, enabled: boolean) => void;
  handleEdit: (workflow: LocalCapabilityType) => void;
}
const MyCapabilitiesRow = ({
  title,
  status,
  prompt,
  trigger,
  scope,
  timeRange,
  keyWords,
  notifications,
  createdAt,
  updatedAt,
  id,
  createdFrom,
  googleDrive,
  handleToggle,
  handleEdit
}: MyCapabilitiesRowProps) => {
  const { deleteWorkflow } = useWorkflow();
  const { showModal } = useModal();
  const queryClient = useQueryClient();
  const listWorkflowsQueryKey = getListWorkflowsQueryKey();

  const onDelete = (id: string | undefined) => {
    if (!id) return;
    const modal = showModal(LazyAreYouSureModal, {
      title: 'Delete capability',
      description: 'Are you sure you want to delete this capability?',
      onSubmit: () => {
        const oldQueryData = queryClient.getQueryData<WorkflowResponse>(listWorkflowsQueryKey);
        queryClient.setQueryData(listWorkflowsQueryKey, (old: WorkflowResponse) =>
          old
            ? {
                ...old,
                items: old.items?.filter(workflow => workflow.id !== id)
              }
            : old
        );
        deleteWorkflow(
          { id },
          {
            onSuccess: () => {
              queryClient.invalidateQueries({ queryKey: listWorkflowsQueryKey });
            },
            onError: () => {
              queryClient.setQueryData(listWorkflowsQueryKey, oldQueryData);
            }
          }
        );
        modal.hide();
      }
    });
  };

  const handleEditWorkflow = () => {
    handleEdit({
      id,
      title,
      status,
      prompt,
      trigger,
      scope,
      timeRange,
      keyWords,
      notifications,
      createdAt,
      updatedAt,
      createdFrom,
      googleDrive: {
        files: googleDrive?.files ?? []
      }
    });
  };

  return (
    <CapabilityRow onClick={handleEditWorkflow}>
      <Cell>
        <Row>
          {createdFrom ? (
            <IconContainer>
              <img
                src={createdFromToIcon[createdFrom]}
                alt="Logo"
                style={{ width: '16px', height: '16px' }}
              />
            </IconContainer>
          ) : (
            <IconContainer>
              <RiInstanceLine size={20} color={palette.grey[500]} />
            </IconContainer>
          )}
          <NameStack>
            <Text>{title}</Text>
            <SmallText>{prompt}</SmallText>
          </NameStack>
        </Row>
      </Cell>
      <Cell>
        <Text>{triggerDisplay(trigger ?? [])}</Text>
      </Cell>
      <Cell>
        <ToggleActive
          active={status === WorkflowDtoStatus.enabled}
          onChange={(isActive, e) => {
            e?.stopPropagation();
            handleToggle(id, isActive);
          }}
        />
      </Cell>
      <Cell>
        <Text>{stringToTime(updatedAt, true)}</Text>
      </Cell>
      <Cell>
        <IconButton
          onClick={e => {
            e.stopPropagation();
            handleEditWorkflow();
          }}
          aria-label="Edit"
          style={{ background: 'none', border: 'none', cursor: 'pointer', marginRight: 8 }}
        >
          <RiEditLine size={16} color={palette.grey[500]} />
        </IconButton>
        <IconButton
          onClick={e => {
            e.stopPropagation();
            onDelete(id);
          }}
          aria-label="Delete"
          style={{ background: 'none', border: 'none', cursor: 'pointer' }}
        >
          <RiDeleteBinLine size={16} color={palette.grey[500]} />
        </IconButton>
      </Cell>
    </CapabilityRow>
  );
};

export default MyCapabilitiesRow;
